import { useMemo, useState } from "react";
import { Button } from "../../components/ui";

const todayString = () => new Date().toISOString().slice(0, 10);

export const createVoucherFormDefaults = () => ({
  code: "",
  discountType: "",
  discountPercentage: "",
  discountAmount: "",
  createdAt: todayString(),
  updatedAt: "",
  expiryDate: "",
});

export const mapVoucherToFormValues = (voucher) => ({
  code: voucher.code || "",
  discountType: voucher.discountType || "",
  discountValue: voucher.discountValue,
  discountPercentage:
    voucher.discountType === "percentage" ? String(voucher.discountValue) : "",
  discountAmount:
    voucher.discountType === "fixed" ? String(voucher.discountValue) : "",
  createdAt: voucher.createdAt
    ? new Date(voucher.createdAt).toISOString().slice(0, 10)
    : todayString(),
  updatedAt: voucher.updatedAt
    ? new Date(voucher.updatedAt).toISOString().slice(0, 10)
    : "",
  expiryDate: voucher.expiryDate
    ? new Date(voucher.expiryDate).toISOString().slice(0, 10)
    : "",
});

function VoucherForm({
  mode = "create",
  initialValues,
  submitLabel,
  onCancel,
  onSubmit,
  error = "",
  saving = false,
}) {
  const [form, setForm] = useState(
    () => initialValues || createVoucherFormDefaults(),
  );

  const isEdit = mode === "edit";

  const resolvedSubmitLabel = useMemo(() => {
    if (submitLabel) return submitLabel;
    return isEdit ? "Save Changes" : "Create Voucher";
  }, [isEdit, submitLabel]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const discountPercentage =
      form.discountPercentage === "" ? null : Number(form.discountPercentage);
    const discountAmount =
      form.discountAmount === "" ? null : Number(form.discountAmount);

    if (!form.code.trim()) {
      onSubmit({ form, error: "Voucher code is required." });
      return;
    }

    if (!form.discountType) {
      onSubmit({ form, error: "Discount type is required." });
      return;
    }

    if (form.discountType === "percentage" && discountPercentage === null) {
      onSubmit({ form, error: "Discount percentage is required." });
      return;
    }

    if (form.discountType === "fixed" && discountAmount === null) {
      onSubmit({ form, error: "Discount amount is required." });
      return;
    }

    if (
      discountPercentage !== null &&
      (Number.isNaN(discountPercentage) ||
        discountPercentage < 0 ||
        discountPercentage > 100)
    ) {
      onSubmit({
        form,
        error: "Discount percentage must be between 0 and 100.",
      });
      return;
    }

    if (
      discountAmount !== null &&
      (Number.isNaN(discountAmount) || discountAmount < 0)
    ) {
      onSubmit({
        form,
        error: "Discount amount must be 0 or greater.",
      });
      return;
    }

    if (form.expiryDate && form.expiryDate < form.createdAt) {
      onSubmit({
        form,
        error: "Expiry date cannot be earlier than created at date.",
      });
      return;
    }

    const payload = {
      code: form.code.trim(),
      discountType: form.discountType,
      discountPercentage:
        form.discountType === "percentage" ? discountPercentage : undefined,
      discountAmount:
        form.discountType === "fixed" ? discountAmount : undefined,
      createdAt: form.createdAt,
      updatedAt: isEdit ? todayString() : "",
      expiryDate: form.expiryDate || undefined,
    };

    onSubmit({ form, payload });
  };

  return (
    <form className="voucher-form" onSubmit={handleSubmit}>
      {error && (
        <div className="voucher-banner voucher-banner-error">
          {String(error)}
        </div>
      )}

      <div className="voucher-form-grid">
        <label className="voucher-field">
          <span>Code *</span>
          <input
            type="text"
            value={form.code}
            onChange={(e) => handleChange("code", e.target.value.toUpperCase())}
            placeholder="SAVE50"
            required
          />
        </label>

        <label className="voucher-field">
          <span>Discount Type *</span>
          <select
            value={form.discountType}
            onChange={(e) => handleChange("discountType", e.target.value)}
          >
            <option value="">Select type</option>
            <option value="percentage">Percentage</option>
            <option value="fixed">Fixed Amount</option>
          </select>
        </label>

        {form.discountType === "percentage" && (
          <label className="voucher-field">
            <span>Discount Percentage *</span>
            <input
              type="number"
              min="0"
              max="100"
              value={form.discountPercentage}
              onChange={(e) =>
                handleChange("discountPercentage", e.target.value)
              }
              required
            />
          </label>
        )}

        {form.discountType === "fixed" && (
          <label className="voucher-field">
            <span>Discount Amount *</span>
            <input
              type="number"
              min="0"
              value={form.discountAmount}
              onChange={(e) => handleChange("discountAmount", e.target.value)}
              placeholder="Fixed amount"
              required
            />
          </label>
        )}

        <label className="voucher-field voucher-field-full">
          <span>Expiry Date</span>
          <input
            type="date"
            value={form.expiryDate}
            onChange={(e) => handleChange("expiryDate", e.target.value)}
          />
        </label>
      </div>

      <div className="voucher-form-actions">
        <Button variant="ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {resolvedSubmitLabel}
        </Button>
      </div>
    </form>
  );
}

export default VoucherForm;
